import React, { useCallback, useEffect, useState } from "react";
import { Button } from "design-system/components/button/button";
import { ButtonSize } from "design-system/components/button/button.types";
import { Icon } from "design-system/components/icon/icon";
import { LoadingSpinner } from "components/icons/loading-spinner";
import NotAvailableImage from "public/assets-v2/illustrations/header_not_available.svg";
import { getPracticeHistory } from "services/api/practice/practiceService";
import type { PracticeHistoryData } from "services/api/practice/practiceService.types";
import { trackPracticeError } from "services/api/app-events/practiceEvents";
import { PracticeErrorScreen } from "../practice-error/practice-error";
import {
  EmptyButtonGroup,
  EmptyDesc,
  EmptyImage,
  EmptyStateContainer,
  EmptyTextContainer,
  EmptyTitle,
  HistoryButtonGroup,
  HistoryList,
  HistoryListContainer,
  HistoryLoadingContainer,
  HistoryModalContent,
  ListPeriod
} from "./practice-history.styles";

interface PracticeHistoryProps {
  accessToken: string;
  onClose: () => void;
  onStartPractice: () => void;
  onSelectHistory: (historyId: string) => void;
  onViewStateChange: (state: PracticeHistoryViewState) => void;
}

export type PracticeHistoryViewState = "loading" | "error" | "empty" | "list";

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });

const formatPeriod = (history: PracticeHistoryData) =>
  `${formatDate(history.start_date)} - ${formatDate(history.end_date)}`;

export const PracticeHistory: React.FC<PracticeHistoryProps> = ({
  accessToken,
  onClose,
  onStartPractice,
  onSelectHistory,
  onViewStateChange
}) => {
  const [histories, setHistories] = useState<PracticeHistoryData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const loadHistories = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);

    if (!accessToken) {
      setHistories([]);
      setHasError(true);
      setIsLoading(false);
      return;
    }

    try {
      const response = await getPracticeHistory(accessToken, 10, 0);
      setHistories(response.data);
    } catch (error) {
      trackPracticeError(
        accessToken,
        "PracticeHistory",
        error instanceof Error ? error.message : String(error),
        "load"
      );
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadHistories();
  }, [loadHistories]);

  const handleStartPractice = () => {
    onClose();
    onStartPractice();
  };

  const handleSelectHistory = (historyId: string) => {
    onClose();
    onSelectHistory(historyId);
  };

  const isEmpty = !isLoading && !hasError && histories.length === 0;
  const hasHistories = !isLoading && !hasError && histories.length > 0;

  useEffect(() => {
    if (isLoading) {
      onViewStateChange("loading");
    } else if (hasError) {
      onViewStateChange("error");
    } else if (isEmpty) {
      onViewStateChange("empty");
    } else {
      onViewStateChange("list");
    }
  }, [hasError, isEmpty, isLoading, onViewStateChange]);

  return (
    <HistoryModalContent>
      {isLoading && (
        <HistoryLoadingContainer>
          <LoadingSpinner variant="ring" />
        </HistoryLoadingContainer>
      )}

      {hasError && <PracticeErrorScreen isEmbedded onRetry={loadHistories} />}

      {isEmpty && (
        <>
          <EmptyStateContainer>
            <EmptyImage
              src={NotAvailableImage}
              alt="Belum ada riwayat latihan"
            />
            <EmptyTextContainer>
              <EmptyTitle>Belum ada riwayat latihan</EmptyTitle>
              <EmptyDesc>
                Mulai latihan pertamamu, kalau udah selesai nanti muncul di sini
              </EmptyDesc>
            </EmptyTextContainer>
          </EmptyStateContainer>

          <EmptyButtonGroup>
            <Button
              appearance="general"
              variant="primary"
              size={ButtonSize.lg}
              isFullWidth
              onClick={handleStartPractice}
            >
              Latihan sekarang
              <Icon name="navigation-arrow-right" />
            </Button>
            <Button
              appearance="general"
              variant="secondary"
              size={ButtonSize.lg}
              isFullWidth
              onClick={onClose}
            >
              Nanti dulu
            </Button>
          </EmptyButtonGroup>
        </>
      )}

      {hasHistories && (
        <>
          <HistoryListContainer>
            {histories.map((history) => (
              <HistoryList
                key={history.id}
                type="button"
                onClick={() => handleSelectHistory(history.id)}
              >
                <ListPeriod>{formatPeriod(history)}</ListPeriod>
                <Icon name="navigation-chevron-right" />
              </HistoryList>
            ))}
          </HistoryListContainer>

          <HistoryButtonGroup>
            <Button
              appearance="general"
              variant="primary"
              size={ButtonSize.lg}
              isFullWidth
              onClick={onClose}
            >
              Kembali
            </Button>
          </HistoryButtonGroup>
        </>
      )}
    </HistoryModalContent>
  );
};
